import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MailX, Loader2, Check } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { api } from "@/lib/api";

// Public (no login) unsubscribe landing page. The signed token in the URL is the authorization.
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // "type" | "all"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("This unsubscribe link is invalid."); return; }
    api.unsubscribeInfo(token).then(async (i) => {
      setInfo(i);
      try { await api.unsubscribe(token, false); setDone("type"); } catch (e) { setErr(e.message); }
    }).catch((e) => setErr(e.message));
  }, [token]);

  const unsubAll = async () => {
    setBusy(true);
    try { await api.unsubscribe(token, true); setDone("all"); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-am-bg px-4" data-testid="unsubscribe-page">
      <div className="w-full max-w-md rounded-3xl border border-am-border bg-am-surface p-8 text-center">
        <Logo size={56} className="mx-auto" />
        {err ? <p className="mt-6 text-sm text-aura-magenta" data-testid="unsubscribe-error">{err}</p>
          : !info || !done ? <div className="mt-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>
          : (
            <>
              <span className="mx-auto mt-6 grid h-12 w-12 place-items-center rounded-2xl border border-aura-mint/40 bg-aura-mint/10 text-aura-mint">{done === "all" ? <MailX className="h-6 w-6" /> : <Check className="h-6 w-6" />}</span>
              <h1 className="mt-4 font-display text-xl font-extrabold text-white" data-testid="unsubscribe-title">
                {done === "all" ? "You won't get any more AuraMax emails." : `You won't get ${info.label} emails anymore.`}
              </h1>
              <p className="mt-2 text-sm text-am-text2">@{info.username} · You'll still see everything in your in-app notifications.</p>
              <div className="mt-6 flex flex-col gap-2">
                {done !== "all" && <button onClick={unsubAll} disabled={busy} data-testid="unsubscribe-all" className="rounded-full border border-am-border-strong py-3 text-sm font-semibold text-white hover:bg-white/5 disabled:opacity-60">Unsubscribe from all AuraMax emails</button>}
                <button onClick={() => navigate("/settings?section=notifications")} data-testid="unsubscribe-manage" className="rounded-full aura-gradient py-3 text-sm font-bold text-black">Manage settings</button>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
